import { useMemo } from "react";
import { DataTable } from "@/components/ui/table/data-table";
import { ColumnDef } from "@tanstack/react-table";
import { displayValue } from "@/lib/helper";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import { ErrorType, SearchCriteriaType } from "@/types";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import { useParams } from "react-router-dom";
import { useGetLinkedMembersQuery } from "@/services/invoiceApi";
import { LinkedMemberMembership, MemberTableDatatypes } from "@/types/member";
import { LinkedMembersInvoiceItem } from "@/types/linked-members";
import { status } from "@/lib/constants/global";
import { Badge } from "@/components/ui/badge";
import useTableData from "@/components/ui/table/data-table-logic";
import { toast } from "@/components/ui/use-toast";
import { useUpdateMembershipStatusMutation } from "@/services/memberApi";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MembershipStatus } from "@/types/enums";
type BusinessDetailProps = {
  memberInfo: MemberTableDatatypes | undefined;
};

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
};
const LinkedMembersTable = ({ memberInfo }: BusinessDetailProps) => {
  // invoice table for linked members
  const { memberId } = useParams();

  const { searchCriteria, query, toggleSortOrder, setSearchCriteria } =
    useTableData(initialValue);
  const { data: linkedMembers, isLoading, refetch } = useGetLinkedMembersQuery(
    { id: Number(memberId), query: query },
    {
      skip: !memberId,
    }
  );

  const linkedMembersTableData = useMemo(() => {
    return Array.isArray(linkedMembers?.data) ? linkedMembers?.data : [];
  }, [linkedMembers]);

  console.log({ linkedMembers, linkedMembersTableData });
  const [updateMembership] = useUpdateMembershipStatusMutation()

  const handleStatusChange = async (payload: LinkedMemberMembership) => {
    try {
      const resp = await updateMembership(payload).unwrap();
      refetch();
      if (resp) {
        toast({
          variant: "success",
          title: "Staff Updated Successfully",
        });
      }
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `${typedError.data?.detail ?? typedError.data?.message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  const columns: ColumnDef<LinkedMembersInvoiceItem>[] = [
    {
      accessorKey: "member_name",
      meta: "member_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Member Name"
          sortKey="name"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="capitalize flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row?.original?.member_name)}
          </div>
        );
      },
    },
    {
      accessorKey: "membership_plan_name",
      meta: "Membership Plan Name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Membership Plan Name"
          sortKey="plan_name"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <Badge className="text-yellow-600 bg-yellow-100 font-normal capitalize">
            {displayValue(row?.original?.membership_plan_name)}
          </Badge>
        );
      },
    },
    {
      accessorKey: "Status",
      meta: "Status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="plan_status"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const value = row.original?.membership_plan_status
        const statusLabel = status.filter((r) => r.value === value)[0];
        const { member_id, id, membership_plan_id } = row.original
        return (
          <Select
            defaultValue={value}
            onValueChange={(e: MembershipStatus) =>
              handleStatusChange({
                client_id: member_id,
                contract_id: id,
                membership_plan_id,
                status: e
              })
            }
          >
            <SelectTrigger className="h-8 w-36">
              <SelectValue placeholder="Status" className="text-gray-400">
                <span className="flex gap-2 items-center">
                  <span
                    className={`${statusLabel?.color} rounded-[50%] w-4 h-4`}
                  ></span>
                  <span>{statusLabel?.label}</span>
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {status.map(
                (item: any) =>
                  !item.hide && (
                    <SelectItem key={item.value + ""} value={item.value + ""}>
                      {item.label}
                    </SelectItem>
                  )
              )}
            </SelectContent>
          </Select>
        );
      },
    },
  ];

  const totalRecords = linkedMembers?.filtered_counts ?? 0;
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });
  return (
    <>
      {/* search bar */}
      <div className="m-2 ">
        <DataTable
          columns={columns}
          data={linkedMembersTableData}
          isLoading={isLoading}
        />
      </div>
      {/* pagination */}
      {linkedMembersTableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}
    </>
  );
};

export default LinkedMembersTable;
